import { handleError } from 'utils/error'
import axios from '../base'
import { PostProgramRequest } from './program.types'
import {
  DiscardProgramRequest,
  DiscardProgramResponse,
  UpdateProgramRequest,
  UpdateProgramResponse,
  GetProgramByIdRequest,
  GetProgramByIdResponse,
  GetProgramPublicResponse,
  GetProgramRegistrationsRequest,
  GetProgramRegistrationsResponse,
  GetProgramScheduleRequest,
  GetProgramScheduleResponse,
  GetProgramsPublicRequest,
  GetProgramsRequest,
  GetProgramsResponse,
} from './program.types'

export class ProgramService {
  private url = '/locations'

  getPrograms = async (
    fields: GetProgramsRequest
  ): Promise<GetProgramsResponse> => {
    const {
      locationId,
      page,
      limit,
      search,
      past,
      visible,
      discarded,
    } = fields
    let endpoint_url = `${this.url}/${locationId}/programs?discarded=${discarded}`
    // Only constrain on visibility when the caller asks (public/player list).
    // The staff list omits it so the backend returns every program (be#212).
    if (visible !== undefined) {
      endpoint_url += `&visible=${visible}`
    }
    // Season window (Upcoming vs Past). Omitted => backend default (Upcoming).
    if (past !== undefined) {
      endpoint_url += `&past=${past}`
    }
    // The backend `page` is a 0-based offset multiplier (offset = page * limit);
    // the UI is 1-based, so translate. (Sending the 1-based value skipped the
    // first page — a latent off-by-one.)
    if (page) {
      endpoint_url += `&page=${page - 1}`
    }
    if (limit) {
      endpoint_url += `&limit=${limit}`
    }
    if (search) {
      endpoint_url += `&search_text=${encodeURIComponent(search)}`
    }
    const { data } = await axios.get<GetProgramsResponse>(endpoint_url)
    if (data.error) {
      handleError(Error('Error en getPrograms'))
      return {} as GetProgramsResponse
    } else {
      return data
    }
  }

  getProgramById = async (fields: GetProgramByIdRequest) => {
    const { locationId, programId } = fields
    const { data } = await axios.get<GetProgramByIdResponse>(
      `${this.url}/${locationId}/programs/${programId}`
    )
    if (data.error) {
      handleError(Error('Error en getProgramById'))
      return {} as GetProgramByIdResponse
    } else {
      return data
    }
  }

  getProgramRegistrations = async (
    fields: GetProgramRegistrationsRequest
  ): Promise<GetProgramRegistrationsResponse> => {
    const { locationId, programId } = fields
    const { data } = await axios.get<GetProgramRegistrationsResponse>(
      `${this.url}/${locationId}/programs/${programId}/registrations`
    )
    if (data.error) {
      handleError(Error('Error en getProgramRegistrations'))
      return {} as GetProgramRegistrationsResponse
    } else {
      return data
    }
  }

  createProgram = async (fields: PostProgramRequest) => {
    const { locationId, body } = fields
    const { data } = await axios.post<GetProgramByIdResponse>(
      `${this.url}/${locationId}/programs`,
      body
    )
    if (data.error) {
      handleError(Error('Error en createProgram'))
      return {} as GetProgramByIdResponse
    } else {
      return data
    }
  }

  discardProgram = async ({
    locationId,
    programId,
  }: DiscardProgramRequest): Promise<DiscardProgramResponse> => {
    // product#586: soft-delete (discard) a program. The new backend endpoint
    // mirrors discard_appointment (POST .../appointments/delete/<id>) and is
    // staff/owner-manager-gated server-side. The slice owns ALL toasts: the
    // fulfilled handler surfaces the 200-envelope `error`/success and the
    // rejected handler surfaces HTTP failures (403 -> no permission, else
    // generic). Toasting here too would double-fire on the 200-envelope path.
    const { data } = await axios.post<DiscardProgramResponse>(
      `${this.url}/${locationId}/programs/delete/${programId}`
    )
    return data
  }

  updateProgram = async ({
    locationId,
    programId,
    body,
  }: UpdateProgramRequest): Promise<UpdateProgramResponse> => {
    // product#169 (be#205): metadata-only update. POST /programs/<id> (item
    // POST = update; collection POST = create). The slice owns ALL toasts (same
    // as discardProgram) — toasting here would double-fire on the 200-envelope
    // `not_found` path. program_times/schedule are never sent (deferred).
    const { data } = await axios.post<UpdateProgramResponse>(
      `${this.url}/${locationId}/programs/${programId}`,
      body
    )
    return data
  }

  getProgramScheduleById = async (
    fields: GetProgramScheduleRequest
  ): Promise<GetProgramScheduleResponse> => {
    const { locationId, programId } = fields
    // product#641: request `calendar_formatting=false` so each session row is an
    // AppointmentDetail carrying a real `date` (YYYY-MM-DD). With `true` the
    // backend returns calendar-event rows ({ start, end } — no `date`), so the
    // modal formatted `moment(undefined)` = today for every row, rendering today's
    // date repeated once per session. Every other appointment caller uses `false`.
    const { data } = await axios.get<GetProgramScheduleResponse>(
      `${this.url}/${locationId}/appointments?calendar_formatting=false&program_id=${programId}`
    )
    if (data.error) {
      handleError(Error('Error en getProgramScheduleById'))
      return {} as GetProgramScheduleResponse
    }
    // A single-session program comes back as one object (the backend collapses
    // `many` when total_count === 1); normalize to an array so the schedule list
    // can always `.map` (mirrors getAppointments).
    if (data.data && !Array.isArray(data.data)) {
      // @ts-ignore single-session collapse → normalize to array
      data.data = [data.data]
    }
    return data
  }

  getPublicPrograms = async (
    fields: GetProgramsPublicRequest
  ): Promise<GetProgramPublicResponse> => {
    const { locationId, programIds, endDate, searchText } = fields
    const program_ids = programIds || []
    const { data } = await axios.post<GetProgramPublicResponse>(
      `${this.url}/${locationId}/programs/public_programs`,
      {
        end_date: endDate,
        program_ids,
        search_text: searchText,
      }
    )
    return data
  }

}

export default new ProgramService()
